import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ReactiveFormsModule } from '@angular/forms';
import { take } from 'rxjs';
import {
  ShareCalculatorData,
  ShareCalculatorModalComponent,
} from '../share-calculator-modal/share-calculator-modal.component';
import { CommitOnEnterDirective } from 'src/app/shared/directives/commit-on-enter.directive';
import { SelectOnFocusDirective } from 'src/app/shared/directives/select-on-focus.directive';
import { TwoDecimalsDirective } from 'src/app/shared/directives/two-decimals.directive';
import {
  REALLOCATION_V2_UNREALIZED_GAIN_LOSS_COLUMN_ENABLED,
  ReallocationV2Store,
} from '../../state/reallocation-v2.store';

/**
 * Marking positions to sell — the same table for two jobs.
 *
 * **Reallocation / Initial Account (step 1):** what the advisor *keeps* is the point. Unchecked
 * rows move into the advisor-directed Custom Holdings sleeve and checked rows are liquidated to
 * free value for the models.
 *
 * **Distribution (step 3):** what the advisor *raises* is the point. The account is not being
 * restructured — money is leaving it — so unchecked rows simply stay put, and this step exists to
 * cover whatever the model sleeves did not. Hence the pinned cash row: custodial cash is the
 * first place a distribution draws from, and making the advisor sell securities to reach cash
 * that is already sitting there would be absurd.
 *
 * ⚠️ The mechanics are identical and deliberately shared — the sellable rules, the share
 * validation, the block on a checked row with no quantity. Only the framing differs, so only the
 * copy and the footer switch.
 */
@Component({
  selector: 'app-rav2-review-positions-step',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, SelectOnFocusDirective, CommitOnEnterDirective,
    // Dollars, unlike the share counts beside them -- see the cash block in the template.
    TwoDecimalsDirective,
  ],
  templateUrl: './review-positions-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewPositionsStepComponent {
  readonly store = inject(ReallocationV2Store);
  private readonly dialog = inject(MatDialog);

  /**
   * Whether this row can offer the shares-from-an-amount calculator.
   *
   * 🔴 DISTRIBUTION ONLY, and that follows from the rows resting at 0 there. A reallocation
   * pre-fills the whole lot, so its common case needs no arithmetic at all — adding a button to
   * a piloted screen to serve the uncommon one is the change this wizard is under standing
   * orders not to make.
   *
   * ⚠️ Needs a price to divide by, and shares to cap against. A position Orion reports no price
   * for is sold whole or not at all; a button that cannot answer is worse than no button.
   */
  canCalculateShares(row: { sellable: boolean; price: number | null; shares: number | null }): boolean {
    return this.canSellWholeRow(row) && (row.price ?? 0) > 0;
  }

  /**
   * Whether this row can offer SELL ALL.
   *
   * ⚠️ NO PRICE REQUIRED, unlike the calculator beside it. Dividing an amount into shares needs
   * something to divide by; selling the whole position does not, and gating both the same way
   * would hide the simpler control on exactly the rows where it is the ONLY way to sell -- a
   * position Orion prices at nothing is sold whole or not at all.
   */
  canSellWholeRow(row: { sellable: boolean; shares: number | null }): boolean {
    return row.sellable && (row.shares ?? 0) > 0;
  }

  /**
   * Work the share count out from a dollar amount, and write it into the row.
   *
   * ⚠️ TICKS THE ROW as well as filling it. Applying a share count to an unticked row would put
   * a figure in a disabled box that sells nothing — the advisor asked for the sale by asking for
   * the amount, and leaving them to also find the checkbox is the kind of half-done action that
   * reads as a bug.
   *
   * 🟢 Order matters: tick first. The tick handler resets the box to the row's resting value, so
   * setting shares before it would be immediately overwritten with 0.
   *
   * A cancelled dialog closes with null and nothing is written — distinct from closing with 0,
   * which the dialog refuses to do so it cannot blank a count the advisor already had.
   */
  openShareCalculator(index: number, row: {
    ticker: string; description: string | null; price: number | null; shares: number | null;
  }): void {
    const data: ShareCalculatorData = {
      ticker: row.ticker,
      description: row.description,
      price: row.price as number,
      // The sell rows carry no per-security priceAsOf; the whole picture is as of the load, and
      // the dialog simply omits the line when it has nothing to say.
      priceAsOf: null,
      mode: 'sell',
      maxShares: row.shares,
    };

    const ref = this.dialog.open<ShareCalculatorModalComponent, ShareCalculatorData, number | null>(
      ShareCalculatorModalComponent,
      { data, autoFocus: 'first-tabbable' },
    );

    ref.afterClosed()
      .pipe(take(1))
      .subscribe((shares) => {
        if (shares == null || shares <= 0) return;
        const group = this.store.positionGroup(index);
        if (!group) return;
        if (!group.controls.selected.value) group.controls.selected.setValue(true);
        group.controls.sharesToSell.setValue(shares);
      });
  }

  /** Cut before the pilot; the data behind it is untouched. See the flag for why. */
  readonly showGainLoss = REALLOCATION_V2_UNREALIZED_GAIN_LOSS_COLUMN_ENABLED;

  /**
   * What the ⚠️ beside the Cash Positions heading says.
   *
   * ⚠️ ONE STRING, UNCONDITIONALLY (AndyP, 2026-09-21). It was longer and it varied: the firm's
   * full policy wording, plus a sentence appended when the request had a sale under trade-team
   * review, explaining that those proceeds still count toward Custom Holdings. Both are gone --
   * a label on a heading is read at a glance, and a paragraph that changes between requests is
   * not something anyone reads twice to notice.
   */
  readonly cashPolicyTooltip = 'Minimum 1% cash target required for Custom Holdings';

  /**
   * What the ⚠️ beside a row's AVAILABLE figure says.
   *
   * ⚠️ ALSO ONE STRING NOW (AndyP, 2026-09-21), where it used to say something different at
   * $0.00 -- that no cash was available until the account exceeded the target. Bound twice, on
   * the figure and on the icon, so it stays a field rather than a literal in the markup.
   *
   * 🔴 THE SECOND SENTENCE IS THE POINT (AndyP, 2026-09-25): this figure is NOT static, and
   * nothing else on the step says so. `cashTargetHoldBasis` carries KEPT value, so liquidating a
   * position lowers the basis, lowers the hold, and raises this number -- an advisor who reads it
   * once and sells afterwards is looking at a figure that has already moved.
   *
   * ⚠️ "INCREASE", AND ONLY EVER INCREASE. The basis can only FALL as positions are sold, so
   * the hold falls with it and this figure cannot move the other way -- naming the direction is
   * safe here in a way it would not be if selling could cut the cash available.
   *
   * ⚠️ "MAY", NOT WHICH. Not every sale moves it: proceeds held for review stay in the sleeve,
   * so selling into those changes nothing, and buys and the step-4 set-aside are excluded from
   * the basis on purpose. Naming the exceptions is how the old three-line breakdown got removed
   * from the table foot -- "may" is honest without inviting "which ones?".
   */
  readonly cashAvailabilityTooltip =
    'Amount of cash available after the 1% minimum cash target. Amount may increase as you '
    + 'liquidate holdings in this request.';

  /**
   * The two explanations a non-cash row's ⚠️ can carry — this one and {@link reviewProceedsTooltip}.
   *
   * ⚠️ HELD HERE rather than inline in the template because each is bound TWICE — on the icon and
   * on the whole row, so hovering anywhere along the row explains it (AndyP, 2026-09-18). Two
   * copies of a sentence in markup is two chances for them to drift.
   *
   * ⚠️ They are opposites and must not be merged. This one marks a row that CANNOT be sold; the
   * other marks a row that sells perfectly well, whose PROCEEDS are then held.
   *
   * ⚠️ REQUEST-AWARE since a distribution started locking these rows. Before that this branch was
   * effectively unreachable — nothing set `sellable` false — so one generic sentence was fine. A
   * locked row on a distribution now hits it, and "this position cannot be sold" is both vaguer
   * than what the advisor needs and easy to read as a restriction on the security itself.
   *
   * The distribution wording names the actual reason: the proceeds are held for review, so the
   * sale cannot serve this request even though the position is perfectly saleable elsewhere.
   */
  get notSellableTooltip(): string {
    return this.store.isDistribution()
      ? 'Proceeds from this security are held for review, so it cannot be sold to fund this '
        + 'distribution.'
      : 'This position cannot be sold in this request.';
  }

  /**
   * ⚠️ A GETTER, because the ending depends on the request type (AndyP, 2026-09-21).
   * "Not immediately available for investment" is the reallocation's answer and is wrong for a
   * distribution: that money is leaving the account, not being reinvested, so the consequence
   * the advisor needs is that these proceeds cannot fund what they are sending out. Same reason
   * `lede()` above is not one sentence.
   *
   * 🟢 The flag itself already existed on these rows -- alternatives are marked on step 1 in
   * every request type. Only the sentence changes.
   */
  get reviewProceedsTooltip(): string {
    const opening = 'Estimated liquidated proceeds from this security will be under review and not '
      + 'immediately available ';
    return this.store.isDistribution()
      ? opening + 'to fund this distribution.'
      : opening + 'for investment.';
  }

  // 🔴 `onSellAll` AND `onUseAllCash` LIVED HERE AND ARE GONE (AndyP, 2026-09-25). They bridged
  // the two select-all header checkboxes to the store. The checkboxes went with every other tick
  // on this step; `toggleSellAll` and `toggleUseAllCash` stay, because Reset and the SMA commit
  // clear rows through them.

  // `heading()` lived here and is GONE (merge with ACMTECH-939). The per-step <h3> was removed
  // from every step template when the shell started rendering titles once, and the shell already
  // owns the type rule this duplicated: `stepLabelOverrides` maps Distribution's `positions` step
  // to 'Raise Cash from Custom Holdings'. Two sources for one label is how they drift apart --
  // and this comment proved it, still naming the long-gone 'Sell Positions' two renames later.

  /**
   * What checking a box means — and it is not the same claim in the two modes.
   *
   * ⚠️ The reallocation wording ("unchecked positions move to the Custom Holdings sleeve") is
   * actively wrong for a distribution: nothing moves, and telling an advisor their untouched
   * holdings are being re-sleeved would describe a restructure they did not request.
   */
  lede(): string {
    // ⚠️ "Check any positions" and "unchecked" had to go with the checkboxes (AndyP, 2026-09-24
    // for the distribution, 2026-09-25 for the rest): there is nothing to check on this step any
    // more, so the sentence described a control the advisor cannot see.
    //
    // 🟢 THREE TYPES REACH THIS STEP and each gets its own second sentence, because what
    // happens to a position the advisor does NOT sell differs, and that consequence is not
    // obvious from a row left at zero:
    //
    //   Distribution     it stays exactly where it is, so there is nothing to add
    //   Reallocation     it moves into the advisor-directed Custom Holdings sleeve
    //   Initial Account  there is no sleeve yet -- it comes across as the new account is built
    //
    // 🔴 INITIAL ACCOUNT IS BEING SUNSET, and is turned off at the PERMISSION level today
    // (AndyP, 2026-09-25) -- not in this code, which is why nothing here tests a flag for it.
    // ACMTECH-997 already folded it into Reallocation on both request trackers. So this branch is
    // written to be correct rather than polished, and whoever removes the request type should
    // delete it rather than trying to keep it current.
    if (this.store.isInitialAccount()) {
      return 'Sell any positions the new account should not hold. Anything you don\'t sell comes '
        + 'across as an advisor-directed holding.';
    }

    return this.store.isDistribution()
      ? 'Raise cash for this distribution by selling positions or using custodial cash below.'
      : 'Sell positions or use custodial cash below. Anything you don\'t sell moves to the '
        + 'advisor-directed Custom Holdings sleeve.';
  }

  /** Everything raised here — cash taken plus sale proceeds. */
  raisedHere(): number {
    return this.store.cashTaken() + this.store.totalSoldValue();
  }

  // /!\ SIX METHODS SERVED THE REMOVED CUSTODIAL-CASH PANEL and went with it (2026-09-17):
  // onCashTaken, amountInput, takeAllCash, cashShortcut, takeRemainingCash, remainingTooltip.
  // A distribution now draws cash through the same per-position rows a reallocation releases
  // it through, so the shortcuts ("ALL", "take exactly what is left") have no control to write.
  //
  // /!\ sourcedTotal, deltaLabel, deltaClass and isOff went too, and those were ALREADY DEAD --
  // the template stopped calling them when "Sourced $X of $25,000" was dropped in favour of the
  // strip owning the running position. Removed here rather than left as a second thing that
  // looks like it might still matter.
  //
  // `raisedHere()` survives above: it states what THIS step raised, which the footer still shows.

  /**
   * Clear every tick and amount on this step.
   *
   * ⚠️ No confirmation prompt, deliberately. The button is disabled until there is something to
   * clear, so it cannot be hit on an untouched step; what it undoes is a handful of checkboxes
   * rather than typed content; and a dialog on every press would cost more than the rare
   * mis-click it prevents. Revisit if it turns out advisors reach for it by accident.
   */
  reset(): void {
    this.store.resetStepOne();
  }
}
